import { useContext } from 'react'
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import Input from "../../../../customs/FormInput";
import Button from "../../../../customs/Button";
import { NotificationContext } from '../../../../providers/notification';
import apiCallFormData from '../../../../utils/apiFormDataCall';
  
interface Specialization {
    Id: number;
    SpecializationName: string;
    Score: number;
    IsActive: boolean;
    CreatedBy?: string;
    ModifiedBy?: string;
    CreateDate: string;
    LastModified: string;
    IsDeleted: boolean;
  }

interface ComponentProps {
    handleClose: () => void;
}

const CreateSpecialization: React.FC<ComponentProps> = ({ handleClose }) => {
    const queryClient = useQueryClient();
    const { showNotification } = useContext(NotificationContext);

    const postSpecialization = async (data: FormData) => {
        return await(apiCallFormData().post('/SetUp/CreateSpecialization', data));
    };

    const postSpecializationMutation = useMutation(['post-specialization'], postSpecialization, {retry: 0});

    const postSpecializationHandler = async (values: FormikValues, resetForm: () => void) => {
        const formData = new FormData();
        formData.append('SpecializationName', values?.name);
        formData.append('Score', values?.score);
        formData.append('IsActive', 'true');

        try {
            await postSpecializationMutation.mutateAsync(formData, {
                onSuccess: () => {
                    showNotification({
                        message: 'Specialization successfully created',
                        type: "success",
                    });
                    queryClient.refetchQueries(['get-specializations']);
                    handleClose();
                    resetForm();
                }
            })
        }catch(error: any) {
            showNotification({
                message: error?.response?.data?.Message || error?.message,
                type: 'error',
            });
        }
    };

    const validationSchema = Yup.object().shape({
        name: Yup.string().matches(/^[A-Za-z\s!@#$%^&*()/]+$/, 'Only text is allowed').required('required'),
        score: Yup.number().typeError('Only numbers are allowed').required('required')
    });

    const formik = useFormik<FormikValues>({
        initialValues: {
            name: '',
            score: ''
        },
        onSubmit: (values, { resetForm }) => {
            postSpecializationHandler(values, resetForm);
        },
        validationSchema,
    })

    return (
        <FormikProvider value={formik}>
            <form onSubmit={formik.handleSubmit}>
                <FormWrapper>
                    <Field 
                        name='name'
                        type='text'
                        as={Input}
                        label='Name'
                        placeholder='Name'
                    />
                    <Field 
                        name='score'
                        type='number'
                        as={Input}
                        label='Score'
                        placeholder='Score'
                    />
                </FormWrapper>
                <ButtonWrapper>
                    <ButtonContainer>
                        <StyledButton onClick={handleClose} text='Cancel'/>
                        <StyledButton 
                            type="submit" 
                            text={postSpecializationMutation.isLoading ? 'Submitting...' : 'Submit'}
                            disabled={postSpecializationMutation.isLoading}
                        />
                    </ButtonContainer>
                </ButtonWrapper>
            </form>
        </FormikProvider>
    )
};

export default CreateSpecialization;

const FormWrapper = styled('div')`
    margin-inline: 3.5rem;
`

const ButtonWrapper = styled('div')`
    display: flex;
    justify-content: flex-end;
    margin-block: 2.5rem;
    padding-inline: 3.5rem;
`

const ButtonContainer = styled('div')`
    display: flex;
    gap: 2rem;
`

const StyledButton = styled(Button)`
    padding-block: 1rem;
    padding-inline: 2rem;
`

